import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';

import { logger } from '@common/logger';
import { BellIcon } from '@common/svg/icons/components/BellIcon';
import { CrossIcon } from '@common/svg/icons/components/CrossIcon';
import { type IconProps } from '@common/svg/icons/components/Icon';
import { LikeIcon } from '@common/svg/icons/components/LikeIcon';

import styles from './FlashMessage.module.scss';

export type Type = 'success' | 'warning' | 'error';

export interface PublicProps {
    type?: Type;
    title?: string;
    message?: string;
    icon?: React.ComponentType<IconProps>;
    isClosable?: boolean;
    delayClose?: number;
}

export interface Props {
    id: number;
    type?: Type;
    title: string;
    message?: string;
    icon?: React.ComponentType<IconProps>;
    isClosable?: boolean;
    delayClose?: number;
    removeMessage: (id: number) => any;
}

export const FlashMessage = ({
    id,
    type,
    title,
    message,
    icon,
    isClosable,
    delayClose,
    removeMessage,
}: Props) => {
    const flashMessageRef = useRef<HTMLLIElement>(null);
    const classNames = ['FlashMessage', styles.FlashMessage];

    useEffect(() => {
        gsap.fromTo(flashMessageRef.current, { x: '-200' }, { x: '0' });

        if (delayClose) {
            setTimeout(() => {
                logger.debug('FlashMessage::timeout', id);
                removeMessage(id);
            }, delayClose);
        }
    });

    if (type === 'error') {
        classNames.push(styles.error);
        if (!icon) icon = BellIcon;
    } else if (type === 'warning') {
        classNames.push(styles.warning);
        if (!icon) icon = BellIcon;
    } else if (type === 'success') {
        classNames.push(styles.success);
        if (!icon) icon = LikeIcon;
    }
    if (!icon) icon = CrossIcon;
    const IconComponent = icon;

    return (
        <li key={id} id={String(id)} className={classNames.join(' ')} ref={flashMessageRef}>
            {isClosable ? <CrossIcon onClick={() => removeMessage(id)} /> : null}
            <IconComponent />
            <h1>{title}</h1>
            {message ? <p>{message}</p> : null}
        </li>
    );
};
